'use client';

import React, { type ForwardedRef, forwardRef, useMemo } from 'react';
import { Text } from 'components/Text';
import { motion } from 'framer-motion';
import {
  IconCheckCircleFill,
  IconInfoFill,
  IconWarningFill,
  IconXCircleFill,
} from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import styles from './Toast.module.css';
import { ToastButtonGroup, ToastCloseButton } from './ToastButton';
import { type ToastAlertVariants, type ToastContentsProps } from './ToastTypes';

function ToastAlertContents(
  {
    adornmentAfter,
    onRequestClose,
    variant = 'neutral',
    closeLabel,
    title,
    details,
    adornmentBefore,
    isLoading = false,
  }: ToastContentsProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const actions = useMemo(() => {
    if (!adornmentAfter) return null;

    const renderedAdornment =
      typeof adornmentAfter === 'function'
        ? adornmentAfter(onRequestClose)
        : adornmentAfter;

    return <ToastButtonGroup>{renderedAdornment}</ToastButtonGroup>;
  }, [adornmentAfter, onRequestClose]);

  return (
    <motion.div
      ref={forwardedRef}
      animate={{
        opacity: 1,
      }}
      className={styles.toastMotionBox}
      data-has-horizontal-boundaries={true}
      data-loading={isLoading || undefined}
      exit={{
        opacity: 0,
      }}
      initial={{
        opacity: 0,
      }}
      transition={{ duration: 0.45 }}
    >
      {isLoading && (
        <div
          className={styles.toastLoadingIndicator}
          data-chromatic="ignore"
          data-testid="toast-alert--loading-indicator"
        />
      )}
      {(variant !== 'neutral' || adornmentBefore) && (
        <div className={styles.toastAdornmentBeforeContainer}>
          {adornmentBefore || <IconForVariant variant={variant} />}
        </div>
      )}
      <div className={styles.toastDisplayMessages}>
        <Text
          color="primary"
          data-testid="toast-alert--text-primary"
          fontWeight="medium"
          style={{
            overflowWrap: 'anywhere',
          }}
          variant="body"
        >
          {title}
        </Text>
        {details && (
          <Text
            color="tertiary"
            data-testid="toast-alert--text-secondary"
            style={{
              overflowWrap: 'anywhere',
            }}
            variant="body"
          >
            {details}
          </Text>
        )}
      </div>
      <div className={styles.toastActions}>
        {actions}
        {closeLabel !== undefined && (
          <ToastCloseButton label={closeLabel} onPress={onRequestClose} />
        )}
      </div>
    </motion.div>
  );
}

const _ToastContents = forwardRef(ToastAlertContents);
_ToastContents.displayName = 'ToastContents';
export { _ToastContents as ToastContents };

/**
 * A subcomponent that renders an icon based on the variant
 */
function IconForVariant({ variant }: { variant: ToastAlertVariants }) {
  switch (variant) {
    case 'success':
      return (
        <IconCheckCircleFill color={theme.colors.icon.interactive.success} />
      );

    case 'info':
      return <IconInfoFill color={theme.colors.brand.primary} />;

    case 'warning':
      return <IconWarningFill color={theme.colors.icon.interactive.notice} />;

    case 'error':
      return <IconXCircleFill color={theme.colors.icon.interactive.error} />;

    default:
      return null;
  }
}
